import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import taskService, { Task } from '../services/taskService';
import outreachInboxService, { InboxSummary } from '../services/outreachInboxService';
import crmService, { Deal, PipelineSummary } from '../services/crmService';
import quoteService, { Quote } from '../services/quoteService';
import mailboxService, { Kennzahlen as PostfachKennzahlen } from '../services/mailboxService';

/**
 * Zahlen fuer das Vertriebs-Cockpit, parallel geladen. Jeder Teil ist
 * einzeln abgesichert -- ein Dienst, der gerade nicht antwortet (oder ein
 * automockter Service im Test), laesst die anderen Kacheln stehen.
 */
export interface SalesOverview {
  aufgaben: { ueberfaellig: number; heute: number; offen: number; liste: Task[] } | null;
  antworten: InboxSummary | null;
  pipeline: PipelineSummary | null;
  faellig: Deal[];
  angebote: { entwurf: number; verschickt: number; angenommen: number; abgelaufen: number; offenerWert: number } | null;
  postfaecher: PostfachKennzahlen | null;
  fehler: string[];
  loadedAt?: number;
}

export const EMPTY_SALES_OVERVIEW: SalesOverview = {
  aufgaben: null,
  antworten: null,
  pipeline: null,
  faellig: [],
  angebote: null,
  postfaecher: null,
  fehler: [],
};

async function safe<T>(name: string, fn: () => Promise<T> | T | undefined, errors: string[]): Promise<T | null> {
  try {
    const v = await Promise.resolve().then(fn);
    return (v ?? null) as T | null;
  } catch (e: any) {
    errors.push(`${name}: ${e?.response?.data?.error || e?.message || 'Fehler'}`);
    return null;
  }
}

/**
 * Welche Teile die Person lesen darf (Welle 2): Angebote liegen unter
 * angebot.lesen, Postfaecher im Versand-Router unter versand.lesen. Ohne
 * Recht wird gar nicht erst gefragt, sonst stuende ein 403 im Cockpit.
 */
export interface SalesOverviewTeile {
  angebote?: boolean;
  postfaecher?: boolean;
}

export async function loadSalesOverview(teile: SalesOverviewTeile = {}): Promise<SalesOverview> {
  const { angebote: mitAngeboten = true, postfaecher: mitPostfaechern = true } = teile;
  const fehler: string[] = [];
  const [tag, antworten, pipeline, faellig, quotes, postfaecher] = await Promise.all([
    safe('Aufgaben', () => taskService.heute(), fehler),
    safe('Antworten', () => outreachInboxService.getSummary(), fehler),
    safe('Pipeline', () => crmService.getSummary(), fehler),
    safe('Fällige Vorgänge', () => crmService.getDue(), fehler),
    mitAngeboten ? safe('Angebote', () => quoteService.list(), fehler) : Promise.resolve(null),
    mitPostfaechern ? safe('Postfächer', () => mailboxService.list(), fehler) : Promise.resolve(null),
  ]);

  const quoteList: Quote[] = Array.isArray(quotes) ? quotes : [];
  const zaehle = (status: string) => quoteList.filter((q) => q.status === status).length;

  return {
    aufgaben: tag && tag.summe
      ? {
        ueberfaellig: tag.summe.ueberfaellig,
        heute: tag.summe.heute,
        offen: tag.summe.offen,
        liste: [...(tag.ueberfaellig ?? []), ...(tag.heute ?? [])].slice(0, 6),
      }
      : null,
    antworten: antworten ?? null,
    pipeline: pipeline ?? null,
    faellig: Array.isArray(faellig) ? faellig.slice(0, 6) : [],
    angebote: quotes
      ? {
        entwurf: zaehle('draft'),
        verschickt: zaehle('sent'),
        angenommen: zaehle('accepted'),
        abgelaufen: zaehle('expired'),
        offenerWert: quoteList.filter((q) => q.status === 'sent').reduce((s, q) => s + (q.netCents || 0), 0),
      }
      : null,
    postfaecher: postfaecher?.kennzahlen ?? null,
    fehler,
    loadedAt: Date.now(),
  };
}

export interface SalesOverviewState {
  data: SalesOverview;
  loading: boolean;
  loaded: boolean;
  refresh: () => Promise<void>;
}

export function useSalesOverview(loader: () => Promise<SalesOverview> = loadSalesOverview): SalesOverviewState {
  const [data, setData] = useState<SalesOverview>(EMPTY_SALES_OVERVIEW);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const result = await loader();
      if (mounted.current) setData(result);
    } catch (e: any) {
      if (mounted.current) setData({ ...EMPTY_SALES_OVERVIEW, fehler: [e?.message || 'Fehler'] });
    } finally {
      if (mounted.current) {
        setLoading(false);
        setLoaded(true);
      }
    }
  }, [loader]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return useMemo(() => ({ data, loading, loaded, refresh }), [data, loading, loaded, refresh]);
}
